/**
 * Site-wide animations — auto scroll-reveal
 * Works with assets/site-animations.css
 *
 * What it does:
 * 1. Automatically finds common elements across the whole site
 *    (headings, section headings, product cards, images, banners, buttons)
 *    and tags them with [data-animate="fade-up"] if they don't already
 *    have a data-animate attribute (so you can still hand-pick animations
 *    per element by adding data-animate="fade-left" etc. directly in Liquid).
 * 2. Uses IntersectionObserver to add .is-visible when scrolled into view.
 * 3. Adds a stagger index to siblings inside the same section/grid so
 *    cards/items animate in one after another instead of all at once.
 * 4. Re-runs safely on Shopify theme editor section reloads.
 */

(function () {
  "use strict";

  var AUTO_SELECTORS = [
    "h1",
    "h2",
    ".section-heading",
    ".hero-banner__content",
    ".hero-banner__media",
    ".product-card",
    ".featured-carousel__header",
    ".featured-carousel .carousel__item, .featured-carousel__grid > *",
    "[data-animate-auto]"
  ].join(",");

  var GROUP_SELECTORS = [
    ".featured-carousel__grid",
    ".featured-carousel theme-carousel .carousel__track",
    ".category-grid",
    ".trust-badges",
    ".testimonials"
  ].join(",");

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  function tagElements(root) {
    root = root || document;

    // Tag stagger groups first so children get an index.
    root.querySelectorAll(GROUP_SELECTORS).forEach(function (group) {
      if (group.hasAttribute("data-animate-group")) return;
      group.setAttribute("data-animate-group", "");
      Array.prototype.forEach.call(group.children, function (child, i) {
        if (!child.hasAttribute("data-animate")) {
          child.setAttribute("data-animate", "fade-up");
        }
        child.style.setProperty("--stagger-index", i);
      });
    });

    // Tag remaining common elements that aren't already handled.
    root.querySelectorAll(AUTO_SELECTORS).forEach(function (el) {
      if (el.closest("[data-animate-group]")) return; // already staggered above
      if (el.hasAttribute("data-animate")) return;
      if (el.closest("[data-animate-skip]")) return;
      el.setAttribute("data-animate", "fade-up");
    });
  }

  function initObserver(root) {
    root = root || document;

    if (prefersReducedMotion()) {
      root.querySelectorAll("[data-animate]").forEach(function (el) {
        el.classList.add("is-visible");
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: "0px 0px -8% 0px",
        threshold: 0.12
      }
    );

    root.querySelectorAll("[data-animate]:not(.is-visible)").forEach(function (el) {
      observer.observe(el);
    });
  }

  function run(root) {
    tagElements(root);
    initObserver(root);
  }

  document.addEventListener("DOMContentLoaded", function () {
    run(document);
  });

  // Re-run when the Shopify theme editor swaps a section in/out,
  // so animations keep working while merchants edit the theme.
  document.addEventListener("shopify:section:load", function (event) {
    run(event.target);
  });
})();